import { getBackendURL } from "../components/env";

/**
 * Attach `timezone_source` (IANA string) to schedule rows so convertScheduledTime
 * can interpret wall times in the row creator's zone.
 *
 * Source of truth is the row's own `notes-c.timezone` column — the creator's zone
 * frozen at insert time (see sql/migrations/add_timezone_column_to_notes_c.sql).
 * The live public.users.timezone lookup is kept only as a fallback for legacy rows
 * that predate the column / haven't been backfilled, so this is safe to ship before
 * the migration is applied. Freezing the zone on the row is what makes display
 * survive the creator being deleted, RLS hiding their users row, or the creator
 * later changing their timezone.
 */
export async function hydrateScheduleRowsTimezoneSource(supabase, rows) {
  if (!rows || rows.length === 0) return rows;

  // Prefer an already-attached source, then the frozen `timezone` column.
  const withColumn = rows.map((r) => ({
    ...r,
    timezone_source: r.timezone_source ?? r.timezone ?? null,
  }));

  // Only rows STILL lacking a source need the (fragile) users lookup.
  const lookupIds = [
    ...new Set(
      withColumn
        .filter((r) => !r.timezone_source)
        .map((r) => r.user_id)
        .filter(Boolean),
    ),
  ];
  if (lookupIds.length === 0) {
    console.log("[tz-debug] hydrateScheduleRowsTimezoneSource summary", {
      rows: rows.length,
      resolvedFromColumn: withColumn.filter((r) => r.timezone_source).length,
      neededUsersLookup: 0,
      finalMissingTimezoneSource: withColumn.filter((r) => !r.timezone_source).length,
    });
    return withColumn;
  }

  let tzMap = {};
  const { data: tzRows } = await supabase
    .from("users")
    .select("id, timezone")
    .in("id", lookupIds);
  if (Array.isArray(tzRows) && tzRows.length > 0) {
    tzMap = Object.fromEntries((tzRows || []).map((u) => [u.id, u.timezone]));
  }

  // Frontend RLS can hide other users' timezone rows; fill gaps via backend service-role endpoint.
  const missingIds = lookupIds.filter((id) => tzMap[id] == null);
  if (missingIds.length > 0) {
    console.log("[tz-debug] direct users lookup missing timezone rows", {
      totalLookupIds: lookupIds.length,
      missingCount: missingIds.length,
      missingIds: missingIds.slice(0, 20),
    });
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const viewerId = sessionData?.session?.user?.id;
      if (viewerId) {
        const backendUrl = getBackendURL();
        const q = new URLSearchParams({
          user_id: viewerId,
          ids: missingIds.join(","),
        });
        const resp = await fetch(
          `${backendUrl}/api/schema-user-timezones/?${q.toString()}`,
        );
        const json = await resp.json().catch(() => ({}));
        const serverMap = json?.timezones || {};
        console.log("[tz-debug] backend timezone fallback response", {
          ok: resp.ok,
          returnedCount: Object.keys(serverMap).length,
          requestedCount: missingIds.length,
        });
        tzMap = { ...tzMap, ...serverMap };
      }
    } catch (_) {
      // non-fatal: caller will fallback to raw times if timezone_source remains null
    }
  }

  const hydrated = withColumn.map((r) =>
    r.timezone_source ? r : { ...r, timezone_source: tzMap[r.user_id] ?? null },
  );
  const finalMissing = hydrated.filter((r) => !r.timezone_source).length;
  console.log("[tz-debug] hydrateScheduleRowsTimezoneSource summary", {
    rows: rows.length,
    resolvedFromColumn: withColumn.filter((r) => r.timezone_source).length,
    neededUsersLookup: lookupIds.length,
    finalMissingTimezoneSource: finalMissing,
  });
  return hydrated;
}

/** Current user's IANA timezone from public.users (for schedule display conversion). */
export async function fetchViewerIanaTimezone(
  supabase,
  fallback = "America/New_York",
) {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const uid = sessionData?.session?.user?.id;
    if (!uid) return fallback;
    const { data } = await supabase
      .from("users")
      .select("timezone")
      .eq("id", uid)
      .maybeSingle();
    if (data?.timezone) {
      console.log("[tz-debug] viewer timezone from direct users lookup", {
        userId: uid,
        timezone: data.timezone,
      });
      return data.timezone;
    }

    // If direct users lookup is blocked by RLS, use backend endpoint.
    const backendUrl = getBackendURL();
    const resp = await fetch(
      `${backendUrl}/api/schema-user-timezones/?${new URLSearchParams({ user_id: uid }).toString()}`,
    );
    const json = await resp.json().catch(() => ({}));
    const resolved = json?.timezones?.[uid] || fallback;
    console.log("[tz-debug] viewer timezone fallback lookup", {
      userId: uid,
      timezone: resolved,
      fromBackend: Boolean(json?.timezones?.[uid]),
    });
    return resolved;
  } catch {
    return fallback;
  }
}
